import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isProblem } from '@rooming/web-core';
import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { passengerApi, type SessionStage } from './api';

// Session state of the app, read from the server (the cookie is HttpOnly: the SPA cannot and must not read it).

interface SessionValue {
  readonly loading: boolean;
  readonly stage: SessionStage | 'ANONYMOUS';
  readonly refresh: () => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | undefined>(undefined);

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return (await passengerApi.session()).data.stage;
      } catch (error) {
        if (isProblem(error) && error.status === 401) return 'ANONYMOUS' as const;
        throw error;
      }
    },
    staleTime: 60_000,
  });
  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['session'] });
  }, [queryClient]);
  const signOut = useCallback(async () => {
    try {
      await passengerApi.logout();
    } catch {
      // the session may already be gone; the local state is cleared anyway
    }
    // Keep the observed session query (the gate re-renders from it) and drop every other cached answer: nothing of the
    // previous user may survive in memory.
    queryClient.setQueryData(['session'], 'ANONYMOUS');
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
  }, [queryClient]);
  return (
    <SessionContext.Provider
      value={{ loading: session.isPending, stage: session.data ?? 'ANONYMOUS', refresh, signOut }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (value === undefined) throw new Error('useSession outside SessionProvider');
  return value;
}
